/**
 * SeriesAppointmentRowActions — per-row kebab for the View/Edit panel's
 * Appointments (recurring-series) tab. Ports Flutter
 * `view_edit_appointment/tab_widget/appointments_tab.dart` `menuOptions()`
 * (:152-357) + the inline `AppointmentStatus` actions.
 *
 * Menu shape (Flutter):
 *   Cancelled / NoShow rows → ONLY "View Appointment".
 *   Active rows, in order:
 *     1. Add Note
 *     2. Edit Appointment
 *     3. (Booked & end in future) Reschedule this / this-and-following / all
 *     4. (not Fulfilled / NoShow) Cancel this / this-and-following / all  (red)
 *
 * Gates (Flutter): Edit/View → `Manage_Patient_Appointments` view OR the
 * current user is the appointment's provider. Reschedule/Cancel → same with
 * edit.
 *
 * Reuses the already-wired shared dialogs (no re-implementation):
 *   - Reschedule → RescheduleAppointmentDialog + useSeriesReschedule for the
 *     following/all scopes (PROD-852 flow), default endpoint for this-only.
 *   - Cancel → CancelAppointmentDialog (its scope picker handles
 *     this/following/all + reason); the menu items pre-select the scope.
 * Edit / View navigate to the occurrence's own panel route.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { APPT_GLYPH, type ApptGlyph } from '@/scheduling/lib/appointment-glyphs';
import { useAddAppointmentNote } from '@/scheduling/services/use-add-appointment-note';
import { useSeriesReschedule } from '@/scheduling/services/use-series-reschedule';
import {
  CancelAppointmentDialog,
  type RecurringScope,
} from '@/scheduling/components/appointments/cancel-appointment-dialog';
import { RescheduleAppointmentDialog } from '@/scheduling/components/appointments/reschedule-appointment-dialog';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import type { AppointmentResponse } from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

const STATUS_BOOKED = 'booked';
const STATUS_FULFILLED = 'fulfilled';
const STATUS_NO_SHOW = 'noshow';
const STATUS_CANCELLED = 'cancelled';

export interface SeriesAppointmentRowActionsProps {
  occ: AppointmentResponse;
  /** `Manage_Patient_Appointments` view — gates Edit / View. */
  canView: boolean;
  /** `Manage_Patient_Appointments` edit — gates Reschedule + Cancel. */
  canEdit: boolean;
  currentUserId: string;
  /** Refetch the series list after a mutation. */
  onChanged: () => void;
}

function providerOf(occ: AppointmentResponse) {
  return occ.participant?.find((p) => {
    const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
    return t === 'provider' || t === 'practitioner';
  })?.provider;
}

export function SeriesAppointmentRowActions({
  occ,
  canView,
  canEdit,
  currentUserId,
  onChanged,
}: SeriesAppointmentRowActionsProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seriesReschedule = useSeriesReschedule();

  const [menuOpen, setMenuOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<RecurringScope | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleScope, setRescheduleScope] = useState<RecurringScope | null>(null);

  const statusCode = (occ.status?.code ?? '').toLowerCase();
  const isInactive = statusCode === STATUS_CANCELLED || statusCode === STATUS_NO_SHOW;
  const provider = providerOf(occ);
  const isOwnProvider = !!provider?.id && provider.id === currentUserId;
  const allowView = canView || isOwnProvider;
  const allowEdit = canEdit || isOwnProvider;

  const isBooked = statusCode === STATUS_BOOKED;
  const endInFuture = !!occ.end && new Date(occ.end).getTime() > practiceNow().getTime();
  const canReschedule = isBooked && endInFuture;
  const canCancel = statusCode !== STATUS_FULFILLED && statusCode !== STATUS_NO_SHOW;

  const patient = findPatientParticipant(occ.participant)?.patient;

  const close = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  const goToPanel = () => {
    if (!allowView) {
      toast.error('You are not authorized to view Appointment');
      return;
    }
    const patientId = patient?.id ?? '';
    const providerId = provider?.id ?? '';
    const profileId =
      occ.appointmentProfile?.referenceId ?? occ.appointmentProfile?.id ?? '';
    const qs = new URLSearchParams({ patientId, providerId, profileId, tab: 'appointments' });
    navigate(`/scheduling/appointment/${occ.id}?${qs.toString()}`);
  };

  const openReschedule = (scope: RecurringScope) => {
    if (!allowEdit) {
      toast.error('You are not authorized to reschedule Appointment');
      return;
    }
    setRescheduleScope(scope);
    setRescheduleOpen(true);
  };

  const openCancel = (scope: RecurringScope) => {
    if (!allowEdit) {
      toast.error('You are not authorized to cancel this appointment');
      return;
    }
    setCancelScope(scope);
    setCancelOpen(true);
  };

  return (
    <>
      <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
        <AnchoredPopoverTrigger
          render={
            <button
              type="button"
              aria-label="Appointment actions"
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted',
                'hover:bg-grey-100 hover:text-text-primary',
              )}
            />
          }
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </AnchoredPopoverTrigger>
        <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-64 p-1 gap-3">
          {isInactive ? (
            <MenuItem icon="appointment" label="View Appointment" onClick={close(goToPanel)} />
          ) : (
            <>
              <MenuItem icon="comment" label="Add Note" onClick={close(() => setNoteOpen(true))} />
              <MenuItem icon="edit" label="Edit Appointment" onClick={close(goToPanel)} />
              {canReschedule ? (
                <>
                  <MenuItem
                    icon="reschedule"
                    label="Reschedule this appointment"
                    onClick={close(() => openReschedule('this-only'))}
                  />
                  <MenuItem
                    icon="reschedule"
                    label="Reschedule this and following appointments"
                    onClick={close(() => openReschedule('following'))}
                  />
                  <MenuItem
                    icon="reschedule"
                    label="Reschedule all appointments in the series"
                    onClick={close(() => openReschedule('all'))}
                  />
                </>
              ) : null}
              {canCancel ? (
                <>
                  <MenuItem
                    label="Cancel only this appointment"
                    destructive
                    onClick={close(() => openCancel('this-only'))}
                  />
                  <MenuItem
                    label="Cancel this and following appointments"
                    destructive
                    onClick={close(() => openCancel('following'))}
                  />
                  <MenuItem
                    label="Cancel all appointments in the series"
                    destructive
                    onClick={close(() => openCancel('all'))}
                  />
                </>
              ) : null}
            </>
          )}
        </AnchoredPopoverContent>
      </AnchoredPopover>

      <AddNoteDialog open={noteOpen} onOpenChange={setNoteOpen} occ={occ} onChanged={onChanged} />

      <RescheduleAppointmentDialog
        open={rescheduleOpen}
        onOpenChange={(next) => {
          setRescheduleOpen(next);
          if (!next) {
            setRescheduleScope(null);
            onChanged();
          }
        }}
        target={{
          appointmentId: occ.id,
          startISO: occ.start ?? '',
          endISO: occ.end ?? '',
        }}
        // following / all → series helper (PUT /api/Appointment/{id} with
        // editRecurring); this-only (or null) → default focused endpoint.
        onSubmitOverride={
          rescheduleScope && rescheduleScope !== 'this-only'
            ? async (newStartISO, newEndISO) => {
                const ok = await seriesReschedule.fire(
                  occ,
                  newStartISO,
                  newEndISO,
                  rescheduleScope,
                );
                if (ok) {
                  queryClient.invalidateQueries({ queryKey: ['recurring-appointment-list'] });
                  onChanged();
                }
                return ok;
              }
            : undefined
        }
      />

      <CancelAppointmentDialog
        open={cancelOpen}
        onOpenChange={(next) => {
          setCancelOpen(next);
          if (!next) {
            setCancelScope(null);
            queryClient.invalidateQueries({ queryKey: ['recurring-appointment-list'] });
            onChanged();
          }
        }}
        initialScope={cancelScope ?? undefined}
        target={{
          appointmentId: occ.id,
          patientId: patient?.id,
          practitionerId: provider?.id,
          startISO: occ.start ?? undefined,
          recurringId: occ.recurring?.id ?? undefined,
          patientFirstName: patient?.firstName,
        }}
      />
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon?: ApptGlyph;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  const Glyph = icon ? APPT_GLYPH[icon] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded px-3 py-1.5 text-left text-sm hover:bg-grey-100',
        destructive ? 'text-status-error' : 'text-text-primary',
      )}
    >
      {Glyph ? <Glyph className="size-4" aria-hidden /> : <span className="w-4" aria-hidden />}
      <span>{label}</span>
    </button>
  );
}

function AddNoteDialog({
  open,
  onOpenChange,
  occ,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  occ: AppointmentResponse;
  onChanged: () => void;
}) {
  const [note, setNote] = useState(occ.comment ?? '');
  const addNote = useAddAppointmentNote();

  // Re-seed each open — the dialog stays mounted across rows.
  useEffect(() => {
    if (open) setNote(occ.comment ?? '');
  }, [open, occ.id, occ.comment]);

  const submit = () => {
    addNote.mutate(
      { id: occ.id, note },
      {
        onSuccess: () => {
          toast.success('Note added');
          onOpenChange(false);
          onChanged();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Add Internal Note"
      size="sm"
      footer={<><Button type="button" size="sm" onClick={submit} disabled={addNote.isPending}>
            Save
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button></>}
    >
        
        <Textarea
          autoFocus
          value={note}
          maxLength={300}
          rows={4}
          placeholder="Add internal note"
          onChange={(e) => setNote(e.target.value)}
        />
        
      </Modal>
  );
}
